import { useMonaco } from '@monaco-editor/react';
import lodash from 'lodash';
import { useEffect } from 'react';

import {
  createAvailableWaiveFormulaFieldsProposals,
  createBaseFieldProposals,
  createCashAdvanceRateAvailableFieldProposals,
  createConcentrationLimitMiscAvailableFieldProposals,
  createEligibilityTestMiscAvailableFieldProposals,
  createFormulaDependencyProposals,
  createIntermediateFieldProposals,
  createMiscConstantFormulaDependencyProposals,
  createMaximumPrefundingAmountAvailableFieldProposals,
  createReceivableAdvanceRateMiscAvailableFieldProposals,
} from '@finley-technologies/calc-agent';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { addMonacoEditorDynamicCompletionItemProperties } from '../ui/common';
import { useCreditAgreementContext } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks/creditAgreementContext';

const { map } = lodash;

// TODO: set up separate constants util for our Feature Flags
// Would be good to create some "config" package to be used more widely for flags
type AvailableFlags = 'isExcelEngineAdvanceRateEnabled';

/**
 * *THIS SHOULD ONLY BE CALLED ONCE IN AN APP AS MONACO-EDITOR IS GLOBAL!*
 * Hook to setup `monaco-editor` language configs, including editors for
 * - `Concentration Limit Config` Editor
 * - Formula Editor for `Intermediate Calcs`, `Eligibility Tests`, `Receivable Valuation Rate` and `Receivable Advance Rate`
 *
 * If you need to extend `monaco-editor` for other parts of admin dashboard, consider creating a new hook that takes into account the language ids already used, including:
 *
 * - `json`
 * - `formula-intermediateCalcs`
 * - `formula-receivableRate`
 * - `formula-eligibility`
 */
export const useSetupMonacoEditor = ({
  intermediateCalcs,
  baseFields,
  jsonSchema,
  flags,
}: {
  intermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[];
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  jsonSchema: any;
  flags?: { [key in AvailableFlags]?: boolean };
}) => {
  const monaco = useMonaco();
  const { orgSlugs } = useCreditAgreementContext();

  useEffect(() => {
    if (monaco) {
      /**
       * Autocomplete Proposals
       */
      const formulaPrettyPrintProposals = createFormulaDependencyProposals({
        prettyPrint: true,
      });
      const miscFormulaPropsals = createMiscConstantFormulaDependencyProposals();
      const baseFieldWithPrefixProposals = createBaseFieldProposals({
        baseFields,
        usePrefix: true,
      });
      const intermediateCalcWithPrefixProposals = createIntermediateFieldProposals(
        {
          intermediateCalcs,
          usePrefix: true,
        },
      );

      /**
       * CashAdvanceRate formulas
       */
      monaco.languages.register({ id: 'formula-cashAdvanceRate' });
      const {
        dispose: disposeCashAdvanceRateCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-cashAdvanceRate',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            // *** Formula Suggestions ***
            const advancedRatePrettyFormulaProposals = createFormulaDependencyProposals(
              {
                useFullFunctions: flags?.isExcelEngineAdvanceRateEnabled,
                prettyPrint: true,
              },
            );
            const formulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              advancedRatePrettyFormulaProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              miscFormulaPropsals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Constant,
              },
            );
            const cashAdvanceRateSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              createCashAdvanceRateAvailableFieldProposals(),
              { range, kind: monaco.languages.CompletionItemKind.Variable },
              { includeRangeVariables: false },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...cashAdvanceRateSuggestions,
              ],
            };
          },
        },
      );

      /**
       * Concentration Limit Configs
       */

      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        schemaValidation: 'warning',
        schemas: [
          {
            uri: 'concentrationLimitConfig',
            fileMatch: ['*'],
            schema: jsonSchema,
          },
        ],
      });
      const {
        dispose: disposeConcentrationLimitCompletionItems,
      } = monaco.languages.registerCompletionItemProvider('json', {
        provideCompletionItems: (model, position) => {
          const concentrationLimitMiscFieldProposals = createConcentrationLimitMiscAvailableFieldProposals();
          const formulaProposals = createFormulaDependencyProposals();
          const textUntilPosition = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });
          const matchFormula = textUntilPosition.match(
            /(?<=.*"formula":\s*)(.*)(?=.*)/,
          );
          const matchField = textUntilPosition.match(
            /(?<=.*"field":\s*)(.*)(?=.*)/,
          );
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };
          if (matchFormula) {
            const formulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              formulaProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              miscFormulaPropsals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Constant,
              },
            );
            const fieldSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              createAvailableWaiveFormulaFieldsProposals(),
              { kind: monaco.languages.CompletionItemKind.Variable, range },
            );
            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...fieldSuggestions,
              ],
            };
          }
          if (matchField) {
            const fieldSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              [
                ...baseFieldWithPrefixProposals,
                ...intermediateCalcWithPrefixProposals,
                ...concentrationLimitMiscFieldProposals,
              ],
              { kind: monaco.languages.CompletionItemKind.Variable, range },
            );

            return {
              suggestions: fieldSuggestions,
            };
          }

          return {
            suggestions: [],
          };
        },
      });

      /**
       * Intermediate Calc formulas
       */
      monaco.languages.register({ id: 'formula-intermediateCalcs' });
      const {
        dispose: disposeIntermediateCalcCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-intermediateCalcs',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            const formulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              formulaPrettyPrintProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              miscFormulaPropsals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Constant,
              },
            );
            const intermediateCalcSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              createIntermediateFieldProposals({
                intermediateCalcs,
                usePrefix: false,
              }),
              { range, kind: monaco.languages.CompletionItemKind.Variable },
            );
            const baseFieldSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              baseFieldWithPrefixProposals,
              { range, kind: monaco.languages.CompletionItemKind.Variable },
            );
            const orgSlugProposals = map(orgSlugs, (slug) => ({
              label: slug,
              documentation: 'string',
              insertText: slug,
            }));
            const orgSlugSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              orgSlugProposals,
              { range, kind: monaco.languages.CompletionItemKind.Variable },
            );
            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...baseFieldSuggestions,
                ...intermediateCalcSuggestions,
                ...orgSlugSuggestions,
              ],
            };
          },
        },
      );

      /**
       * Eligibility Test formulas
       */
      monaco.languages.register({ id: 'formula-eligibilityTest' });
      const {
        dispose: disposeEligibilityTestCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-eligibilityTest',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            const formulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              formulaPrettyPrintProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              miscFormulaPropsals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Constant,
              },
            );
            const eligibilityTestMiscFormulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              createEligibilityTestMiscAvailableFieldProposals(),
              {
                range,
                kind: monaco.languages.CompletionItemKind.Variable,
              },
            );
            const intermediateCalcSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              intermediateCalcWithPrefixProposals,
              { range, kind: monaco.languages.CompletionItemKind.Variable },
            );
            const baseFieldSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              baseFieldWithPrefixProposals,
              { range, kind: monaco.languages.CompletionItemKind.Variable },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...baseFieldSuggestions,
                ...intermediateCalcSuggestions,
                ...eligibilityTestMiscFormulaSuggestions,
              ],
            };
          },
        },
      );

      /**
       * Max Prefunding Amount Formulas
       */
      monaco.languages.register({ id: 'formula-maximumPrefundingAmount' });
      const {
        dispose: disposeMaximumPrefundingAmountCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-maximumPrefundingAmount',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            const formulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              formulaPrettyPrintProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              miscFormulaPropsals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Constant,
              },
            );
            const maximumPrefundingAmountSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              createMaximumPrefundingAmountAvailableFieldProposals(),
              { range, kind: monaco.languages.CompletionItemKind.Variable },
              { includeRangeVariables: false },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...maximumPrefundingAmountSuggestions,
              ],
            };
          },
        },
      );

      /**
       * ReceivableAdvanceRate formulas
       */
      // TODO: create global monaco ID constant variables to replace each hardcoded ID
      // This will also help alleviate and manage potential clashes (currently won't be able to tell)
      monaco.languages.register({ id: 'formula-receivableAdvanceRate' });
      const {
        dispose: disposeReceivableAdvanceRateCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-receivableAdvanceRate',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            // *** Formula Suggestions ***
            const advancedRatePrettyFormulaProposals = createFormulaDependencyProposals(
              {
                useFullFunctions: flags?.isExcelEngineAdvanceRateEnabled,
                prettyPrint: true,
              },
            );
            const formulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              advancedRatePrettyFormulaProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              miscFormulaPropsals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Constant,
              },
            );

            // *** Variable Suggestions ***
            const baseFieldSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              baseFieldWithPrefixProposals,
              { range, kind: monaco.languages.CompletionItemKind.Variable },
              { includeRangeVariables: flags?.isExcelEngineAdvanceRateEnabled },
            );
            const intermediateCalcSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              intermediateCalcWithPrefixProposals,
              { range, kind: monaco.languages.CompletionItemKind.Variable },
              { includeRangeVariables: flags?.isExcelEngineAdvanceRateEnabled },
            );
            const receivableAdvanceRateMiscSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              createReceivableAdvanceRateMiscAvailableFieldProposals(),
              { range, kind: monaco.languages.CompletionItemKind.Variable },
              { includeRangeVariables: flags?.isExcelEngineAdvanceRateEnabled },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...baseFieldSuggestions,
                ...intermediateCalcSuggestions,
                ...receivableAdvanceRateMiscSuggestions,
              ],
            };
          },
        },
      );

      /**
       * ReceivableValuationRate formulas
       */
      monaco.languages.register({ id: 'formula-receivableValuationRate' });
      const {
        dispose: disposeReceivableValuationRateCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-receivableValuationRate',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            const formulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              formulaPrettyPrintProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              miscFormulaPropsals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Constant,
              },
            );
            const intermediateCalcSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              intermediateCalcWithPrefixProposals,
              { range, kind: monaco.languages.CompletionItemKind.Variable },
            );
            const baseFieldSuggestions = addMonacoEditorDynamicCompletionItemProperties(
              baseFieldWithPrefixProposals,
              { range, kind: monaco.languages.CompletionItemKind.Variable },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...baseFieldSuggestions,
                ...intermediateCalcSuggestions,
              ],
            };
          },
        },
      );
      return () => {
        disposeCashAdvanceRateCompletionItems();
        disposeConcentrationLimitCompletionItems();
        disposeIntermediateCalcCompletionItems();
        disposeEligibilityTestCompletionItems();
        disposeMaximumPrefundingAmountCompletionItems();
        disposeReceivableAdvanceRateCompletionItems();
        disposeReceivableValuationRateCompletionItems();
      };
    }
    return () => {};
  }, [monaco, orgSlugs, baseFields, intermediateCalcs, jsonSchema, flags]);
};
